import React from "react";
import { Localization } from "@viamap/viamap2-common";
import { ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { LayerType } from "src/common/managers/Types";
import { AnnotationType, AnnotationContextType, type ANNAnnotationRecord } from "./ANNTypes";
import { MIBIcon } from "src/componentsUtils/MIBIcon";

function isPdfDocument(record: ANNAnnotationRecord): boolean {
   const mime = record.document?.mimeType?.toLowerCase();
   if (mime) {
      return mime === "application/pdf";
   }
   const filename = record.document?.filename?.toLowerCase();
   return !!filename && filename.endsWith(".pdf");
}

function stedfaestelseHasData(record: ANNAnnotationRecord): boolean {
   const layers = record.layers || [];
   return layers.some((layer) => {
      if (layer.type === LayerType.ImageOverlay && layer.geoJson?.features?.length) {
         return true;
      }
      if (layer.type === LayerType.ImageOverlay && layer.imageOverlay?.coordinates?.length) {
         return true;
      }
      if ((layer.type === LayerType.GeoJSON_Line || layer.type === LayerType.GeoJSON_Polygon || layer.type === LayerType.GeoJSON_Point) &&
         layer.geoJson?.features?.length) {
         return true;
      }
      return false;
   });
}

interface PrimaryActionProps {
   record?: ANNAnnotationRecord;
   importingId: string | null;
   downloadingId: string | null;
   onShowNote: (record: ANNAnnotationRecord) => void;
   onImport: (record: ANNAnnotationRecord) => void;
   onOpenDocument?: (record: ANNAnnotationRecord) => void;
   onDownloadDocument: (record: ANNAnnotationRecord) => void;
}

export function ANNAnnotationPrimaryActionCell(props: PrimaryActionProps) {
   const { record, importingId, downloadingId, onShowNote, onImport, onOpenDocument, onDownloadDocument } = props;
   if (!record) {
      return null;
   }
   if (record.type === AnnotationType.Note) {
      return (
         <div className="ann-actions-cell ann-actions-cell--primary">
            <ViewButtonIcon
               onClick={() => onShowNote(record)}
               title={Localization.getText("Show_vis")}
            >
               <MIBIcon icon="show" />
            </ViewButtonIcon>
         </div>
      );
   }
   if (record.type === AnnotationType.Document) {
      const isPdf = isPdfDocument(record);
      const useOpen = isPdf && !!onOpenDocument;
      const disabled = !record.document?.s3Key || downloadingId === record.id;
      const handler = useOpen ? onOpenDocument! : onDownloadDocument;
      const title = useOpen
         ? (downloadingId === record.id
            ? Localization.getText("Loading")
            : Localization.getText("Open document"))
         : (downloadingId === record.id
            ? Localization.getText("Downloading...")
            : Localization.getText("Download"));
      return (
         <div className="ann-actions-cell ann-actions-cell--primary">
            <ViewButtonIcon
               disabled={disabled}
               onClick={() => handler(record)}
               title={title}
            >
               <MIBIcon icon={useOpen ? "pdf" : "download"} />
            </ViewButtonIcon>
         </div>
      );
   }
   const disabled = importingId === record.id || !stedfaestelseHasData(record);
   return (
      <div className="ann-actions-cell ann-actions-cell--primary">
         <ViewButtonIcon
            disabled={disabled}
            onClick={() => onImport(record)}
            title={importingId === record.id ? Localization.getText("Loading") : "tilføj som kortlag"}
         >
            <MIBIcon icon="addToMap" />
         </ViewButtonIcon>
      </div>
   );
}

interface AnnotationActionsCellProps {
   record?: ANNAnnotationRecord;
   importingId: string | null;
   downloadingId: string | null;
   deletingId: string | null;
   editingId: string | null;
   canVerify: boolean;
   canDelete?: boolean;
   canWrite?: boolean;
   contextType?: AnnotationContextType;
   onShowNote: (record: ANNAnnotationRecord) => void;
   onImport: (record: ANNAnnotationRecord) => void;
   onDownloadDocument: (record: ANNAnnotationRecord) => void;
   onDelete: (record: ANNAnnotationRecord) => void;
   onEditNote?: (record: ANNAnnotationRecord) => void;
   onEditStedfaestelse?: (record: ANNAnnotationRecord) => void;
   onEditDocument?: (record: ANNAnnotationRecord) => void;
   onOpenServitutDetails?: (record: ANNAnnotationRecord) => void;
   showServitutDetailsButton?: boolean;
   alwaysShowDownload?: boolean;
   onToggleHistory?: (record: ANNAnnotationRecord) => void;
   isHistoryOpen?: boolean;
   historyAvailable?: boolean;
   /**
    * When true, omits the primary action button (Show note / Open or Download document /
    * Import stedfaestelse) so it can be rendered separately via ANNAnnotationPrimaryActionCell.
    */
   hidePrimary?: boolean;
}
export function ANNAnnotationActionsCell(props: AnnotationActionsCellProps) {
   const { record, importingId, downloadingId, deletingId, editingId, canVerify, canDelete = true, canWrite = false, onShowNote, onImport, onDownloadDocument, onDelete, onEditNote, onEditStedfaestelse, onEditDocument, onOpenServitutDetails, showServitutDetailsButton, alwaysShowDownload, onToggleHistory, isHistoryOpen, historyAvailable = true, hidePrimary = false } = props;
   if (!record) {
      return null;
   }
   const isDeleting = deletingId === record.id;
   const editButtonTitle = canVerify
      ? Localization.getText("Edit & Verify")
      : Localization.getText("Edit annotation");
   if (record.type === AnnotationType.Note) {
      return (
         <div className="ann-actions-cell">
            {onToggleHistory && (
               <ViewButtonIcon
                  disabled={!historyAvailable}
                  onClick={() => historyAvailable && onToggleHistory(record)}
                  title={
                     !historyAvailable
                        ? Localization.getText("No history")
                        : isHistoryOpen
                           ? Localization.getText("Hide history")
                           : Localization.getText("Show history")
                  }
               >
                  <MIBIcon icon="history" />
               </ViewButtonIcon>
            )}
            {showServitutDetailsButton && record.documentId && onOpenServitutDetails && (
               <ViewButtonIcon
                  onClick={() => onOpenServitutDetails(record)}
                  title={Localization.getText("Open servitut details")}
               >
                  <MIBIcon icon="servitutDetails" />
               </ViewButtonIcon>
            )}
            {!hidePrimary && (
               <ViewButtonIcon
                  onClick={() => onShowNote(record)}
                  title={Localization.getText("Show_vis")}
               >
                  <MIBIcon icon="show" />
               </ViewButtonIcon>
            )}
            {canWrite && onEditNote && (
               <ViewButtonIcon
                  disabled={editingId === record.id}
                  onClick={() => onEditNote(record)}
                  title={editingId === record.id ? Localization.getText("Editing...") : editButtonTitle}
               >
                  <MIBIcon icon="edit" />
               </ViewButtonIcon>
            )}
            {canDelete && (
               <ViewButtonIcon
                  disabled={isDeleting}
                  onClick={() => onDelete(record)}
                  title={Localization.getText("Delete")}
               >
                  <MIBIcon icon="delete" />
               </ViewButtonIcon>
            )}
         </div>
      );
   }
   if (record.type === AnnotationType.Document) {
      const showDownload = alwaysShowDownload !== false;
      return (
         <div className="ann-actions-cell">
            {onToggleHistory && (
               <ViewButtonIcon
                  disabled={!historyAvailable}
                  onClick={() => historyAvailable && onToggleHistory(record)}
                  title={
                     !historyAvailable
                        ? Localization.getText("No history")
                        : isHistoryOpen
                           ? Localization.getText("Hide history")
                           : Localization.getText("Show history")
                  }
               >
                  <MIBIcon icon="history" />
               </ViewButtonIcon>
            )}
            {showServitutDetailsButton && record.documentId && onOpenServitutDetails && (
               <ViewButtonIcon
                  onClick={() => onOpenServitutDetails(record)}
                  title={Localization.getText("Open servitut details")}
               >
                  <MIBIcon icon="servitutDetails" />
               </ViewButtonIcon>
            )}
            {showDownload && (
               <ViewButtonIcon
                  disabled={!record.document?.s3Key || downloadingId === record.id}
                  onClick={() => onDownloadDocument(record)}
                  title={downloadingId === record.id ? Localization.getText("Downloading...") : Localization.getText("Download")}
               >
                  <MIBIcon icon="download" />
               </ViewButtonIcon>
            )}
            {canWrite && onEditDocument && (
               <ViewButtonIcon
                  disabled={editingId === record.id}
                  onClick={() => onEditDocument(record)}
                  title={editingId === record.id ? Localization.getText("Editing...") : editButtonTitle}
               >
                  <MIBIcon icon="edit" />
               </ViewButtonIcon>
            )}
            {canDelete && (
               <ViewButtonIcon
                  disabled={isDeleting}
                  onClick={() => onDelete(record)}
                  title={Localization.getText("Delete")}
               >
                  <MIBIcon icon="delete" />
               </ViewButtonIcon>
            )}
         </div>
      );
   }
   const disabled = importingId === record.id || !stedfaestelseHasData(record);
   return (
      <div className="ann-actions-cell">
         {onToggleHistory && (
            <ViewButtonIcon
               disabled={!historyAvailable}
               onClick={() => historyAvailable && onToggleHistory(record)}
               title={
                  !historyAvailable
                     ? (Localization.getText("No history"))
                     : isHistoryOpen
                        ? (Localization.getText("Hide history"))
                        : (Localization.getText("Show history"))
               }
            >
               <MIBIcon icon="history" />
            </ViewButtonIcon>
         )}
         {showServitutDetailsButton && record.documentId && onOpenServitutDetails && (
            <ViewButtonIcon
               onClick={() => onOpenServitutDetails(record)}
               title={Localization.getText("Open servitut details")}
            >
               <MIBIcon icon="servitutDetails" />
            </ViewButtonIcon>
         )}
         {!hidePrimary && (
            <ViewButtonIcon
               disabled={disabled}
               onClick={() => onImport(record)}
               title={importingId === record.id ? Localization.getText("Loading") : "tilføj som kortlag"}
            >
               <MIBIcon icon="addToMap" />
            </ViewButtonIcon>
         )}
         {canWrite && onEditStedfaestelse && (
            <ViewButtonIcon
               disabled={editingId === record.id}
               onClick={() => onEditStedfaestelse(record)}
               title={editingId === record.id ? Localization.getText("Editing...") : editButtonTitle}
            >
               <MIBIcon icon="edit" />
            </ViewButtonIcon>
         )}
         {canDelete && (
            <ViewButtonIcon
               disabled={isDeleting}
               onClick={() => onDelete(record)}
               title={Localization.getText("Delete")}
            >
               <MIBIcon icon="delete" />
            </ViewButtonIcon>
         )}
      </div>
   );
}
